import { useCurrentEditor } from "@tiptap/react";
import { type ReactNode, forwardRef, useEffect, useState } from "react";
import { Button } from "../button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";
import { ToggleGroup, ToggleGroupItem } from "../toggle-group";
import "./rich-text-editor.css";
import { twMerge } from "tailwind-merge";
import { match } from "ts-pattern";
import {
  CheckPoints1,
  DotPoints1,
  Heading1,
  Heading2,
  Heading3,
  NumberPoints1,
  Type1,
  XClose,
} from "../icons";

type BubbleMenuToggleOptions = "bold" | "strike" | "underline" | "italic";

const FormattingMenuContent = forwardRef<
  HTMLDivElement,
  { children: ReactNode }
>(({ children }, ref) => {
  const { editor } = useCurrentEditor();
  const [toggleGroupState, setToggleGroupState] = useState<
    Array<BubbleMenuToggleOptions>
  >([]);

  // The tip tap editor has its own internal state and we want to manage the state of the toggle group within the bubble menu (appears when text is highlighted) to make sure the correct buttons are highlighted based on styles currently applied to the selected text. We therefore have the below useEffect which runs whenever the editor selection is updated or a transaction occurs
  useEffect(() => {
    if (!editor) {
      return;
    }

    const updateToggleState = () => {
      const currentState: Array<BubbleMenuToggleOptions> = [];
      if (editor.isActive("bold")) {
        currentState.push("bold");
      }
      if (editor.isActive("underline")) {
        currentState.push("underline");
      }
      if (editor.isActive("strike")) {
        currentState.push("strike");
      }
      if (editor.isActive("italic")) {
        currentState.push("italic");
      }
      setToggleGroupState(currentState);
    };

    // Initial state
    updateToggleState();

    // Listen to editor updates
    editor.on("selectionUpdate", updateToggleState);
    editor.on("transaction", updateToggleState);

    // Cleanup listeners on unmount
    return () => {
      editor.off("selectionUpdate", updateToggleState);
      editor.off("transaction", updateToggleState);
    };
  }, [editor]);

  const handleValueChange = (value: Array<BubbleMenuToggleOptions>) => {
    if (!editor) {
      return;
    }

    const chain = editor.chain().focus();

    // Apply or remove formatting based on the new value
    if (value.includes("bold")) {
      chain.setBold();
    } else {
      chain.unsetBold();
    }

    if (value.includes("underline")) {
      chain.setUnderline();
    } else {
      chain.unsetUnderline();
    }

    if (value.includes("strike")) {
      chain.setStrike();
    } else {
      chain.unsetStrike();
    }

    if (value.includes("italic")) {
      chain.setItalic();
    } else {
      chain.unsetItalic();
    }

    chain.run();
  };

  return (
    <div
      ref={ref}
      className="flex w-fit rounded-lg bg-white lg:shadow-sm"
      // onFocus={() => {
      //   editor?.chain().focus().run();
      // }}
    >
      <ToggleGroup
        value={toggleGroupState}
        onValueChange={handleValueChange}
        size="sm"
        type="multiple"
      >
        {children}
      </ToggleGroup>
    </div>
  );
});
FormattingMenuContent.displayName = "TipTapBubbleMenuContent";

const FormattingMenuToggleButton = ({
  value,
  children,
}: {
  value: BubbleMenuToggleOptions | "link";
  children: ReactNode;
}) => {
  const { editor } = useCurrentEditor();
  if (editor == null) {
    throw new Error(
      "Bubble Menu Toggle Button must be used within RichTextEditor",
    );
  }
  return (
    <ToggleGroupItem value={value} aria-label={`Toggle ${value}`} size="sm">
      {children}
    </ToggleGroupItem>
  );
};
const FormattingMenuLinkButton = ({
  value,
  children,
}: {
  value: "link";
  children: ReactNode;
}) => {
  const { editor } = useCurrentEditor();
  if (editor == null) {
    throw new Error("Bubble Menu Button must be used within RichTextEditor");
  }

  return (
    <Button
      variant="tertiaryGray"
      className={twMerge(
        "h-8 w-8 shrink-0 px-2 py-0",
        editor.isActive("link") && "text-grey-800 bg-grey-100",
      )}
      size="sm"
      onClick={() => {
        editor.chain().setLink({ href: "" }).run();
      }}
      value={value}
      aria-label={value}
    >
      {children}
    </Button>
  );
};

const FormattingMenuHeadingSelect = ({
  containerRef,
}: {
  children?: ReactNode;
  containerRef?: React.RefObject<HTMLDivElement | null>;
}) => {
  const { editor } = useCurrentEditor();
  if (!editor) {
    return;
  }

  type TextHeadingStyles = "regular" | "h1" | "h2" | "h3";

  const handleValueChange = (value: TextHeadingStyles) => {
    switch (value) {
      case "regular": {
        editor.chain().focus().setParagraph().run();
        break;
      }
      case "h1": {
        console.log("H1");
        editor.chain().focus().setHeading({ level: 1 }).run();
        break;
      }
      case "h2": {
        editor.chain().focus().setParagraph().setHeading({ level: 2 }).run();
        break;
      }
      case "h3": {
        editor.chain().focus().setParagraph().setHeading({ level: 3 }).run();
        break;
      }
      default: {
        editor.chain().focus().setParagraph().run();
      }
    }
  };

  // Determine the current heading style
  let currentValue: TextHeadingStyles = "regular";
  if (editor.isActive("heading", { level: 1 })) {
    currentValue = "h1";
  } else if (editor.isActive("heading", { level: 2 })) {
    currentValue = "h2";
  } else if (editor.isActive("heading", { level: 3 })) {
    currentValue = "h3";
  }

  if (containerRef != null && containerRef.current == null) {
    return;
  }

  return (
    <Select
      value={currentValue}
      onValueChange={handleValueChange}
      defaultValue="regular"
    >
      <SelectTrigger className="w-fit text-grey-500 border-none! pl-2 pr-1 h-8 py-0 outline-hidden! ring-0! [&_.chevron]:size-3.5 rounded-md">
        {match(currentValue)
          .with("h1", () => <Heading1 className="h-4 w-4" />)
          .with("h2", () => <Heading2 className="h-4 w-4" />)
          .with("h3", () => <Heading3 className="h-4 w-4" />)
          .otherwise(() => (
            <Type1 className="h-4 w-4" />
          ))}
      </SelectTrigger>
      <SelectContent
        containerEl={containerRef?.current}
        onCloseAutoFocus={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <SelectGroup>
          <SelectItem className="pl-2 py-1 font-normal h-7" value="regular">
            Regular
          </SelectItem>
          <SelectItem
            className="pl-2 py-1 text-lg font-medium leading-none h-7"
            value="h1"
          >
            H1
          </SelectItem>
          <SelectItem
            className="pl-2 text-md py-1 font-medium leading-none h-7"
            value="h2"
          >
            H2
          </SelectItem>
          <SelectItem
            className="pl-2 py-1 font-medium leading-none h-7"
            value="h3"
          >
            H3
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

const FormattingMenuListSelect = ({
  containerRef,
}: {
  containerRef?: React.RefObject<HTMLDivElement | null>;
}) => {
  const { editor } = useCurrentEditor();
  if (!editor) {
    return;
  }

  type ListStyles = "ol" | "ul" | "task" | "none";

  const handleValueChange = (value: ListStyles) => {
    switch (value) {
      case "ul": {
        editor.chain().focus().toggleBulletList().run();
        break;
      }
      case "ol": {
        editor.chain().focus().toggleOrderedList().run();
        break;
      }
      case "task": {
        editor.chain().focus().toggleTaskList().run();
        break;
      }
      case "none": {
        editor.chain().focus().setParagraph().run();
        return;
      }
    }
  };

  let currentValue: ListStyles = "none";
  if (editor.isActive("taskList")) {
    currentValue = "task";
  } else if (editor.isActive("orderedList")) {
    currentValue = "ol";
  } else if (editor.isActive("bulletList")) {
    currentValue = "ul";
  }

  if (containerRef != null && containerRef.current == null) {
    return;
  }

  return (
    <Select
      value={currentValue}
      defaultValue="none"
      onValueChange={handleValueChange}
    >
      <SelectTrigger className="active:bg-grey-100 text-grey-500 w-fit border-none! pl-2 pr-1 h-8 py-0 outline-hidden! ring-0! [&_.chevron]:size-3.5 rounded-md">
        <SelectValue>
          {match(currentValue)
            .with("ul", () => <DotPoints1 className="h-4 w-4" />)
            .with("ol", () => <NumberPoints1 className="h-4 w-4" />)
            .with("task", () => <CheckPoints1 className="h-4 w-4" />)
            .otherwise(() => (
              <DotPoints1 className="h-4 w-4" />
            ))}
        </SelectValue>
      </SelectTrigger>
      <SelectContent
        containerEl={containerRef?.current}
        className="pointer-events-auto"
        onCloseAutoFocus={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <SelectGroup>
          {currentValue != "none" && (
            <SelectItem className="py-1 pl-2" value="none">
              <XClose className="h-4 w-4 text-grey-500" />
              Remove list
            </SelectItem>
          )}
          <SelectItem className="py-1 pl-2" value="ul">
            <DotPoints1 className="h-4 w-4 text-grey-500" />
            List
          </SelectItem>
          <SelectItem className="py-1 pl-2" value="ol">
            <NumberPoints1 className="h-4 w-4 text-grey-500" />
            Ordered list
          </SelectItem>
          <SelectItem className="py-1 pl-2" value="task">
            <CheckPoints1 className="h-4 w-4 text-grey-500" />
            Checklist
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

export {
  FormattingMenuContent,
  FormattingMenuToggleButton,
  FormattingMenuLinkButton as FormattingMenuButton,
  FormattingMenuHeadingSelect,
  FormattingMenuListSelect,
};
